import { createMuiTheme, fade } from '@material-ui/core/styles'
import * as fonts from 'assets/fonts'

const defaultTheme = createMuiTheme({})

const palette = {
  primary: {
    dark: '#1a1732',
    main: '#374293',
    light: '#6551ff',
    lighter: '#ebedfb',
  },
  secondary: {
    dark: '#d4a532',
    main: '#ffce36',
    light: '#fae920',
    lighter: '#fef7ca',
  },
  error: {
    dark: '#c91c32',
    main: '#eb6576',
    light: '#fdc0bf',
  },
  success: {
    dark: '#00b0a6',
    main: '#90cfd4',
  },
  grey: {
    100: '#efefef',
    200: '#6c717a',
    300: '#484c52',
    400: '#212121',
  },
  action: {
    disabled: '#ffffff',
    disabledBackground: '#6c717a',
  },
  text: {
    secondary: '#6c717a',
  },
}

const typography = {
  fontFamily: [
    'Avenir Next',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  h1: {
    fontFamily: fonts.avenirBold,
    fontSize: 20,
    fontWeight: 700,
    [defaultTheme.breakpoints.up('md')]: {
      fontSize: 24,
    },
  },
  h2: {
    fontFamily: fonts.avenirBold,
    fontSize: 16,
    fontWeight: 700,
    [defaultTheme.breakpoints.up('md')]: {
      fontSize: 20,
    },
  },
  body1: {
    fontWeight: 500,
  },
  body2: {
    fontWeight: 500,
  },
  subtitle1: {
    fontSize: 12,
    fontWeight: 500,
    lineHeight: 1.33,
  },
  overline: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 1,
  },
}

const shadows = [
  'none',
  '0 1px 3px 0 rgba(33, 33, 33, 0.2), 0 2px 4px 0 rgba(33, 33, 33, 0.08)',
  '0 2px 4px -8px rgba(33, 33, 33, 0.2), 0 2px 6px 2px rgba(33, 33, 33, 0.12)',
  '0 11px 15px -6px rgba(33, 33, 33, 0.24), 0 9px 46px 8px rgba(33, 33, 33, 0.12), 0 24px 38px 4px rgba(33, 33, 33, 0.16)',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
  'none',
]

const theme = createMuiTheme({
  palette,
  typography,
  sidebarWidth: defaultTheme.spacing(32),
  sidebarWidthClose: defaultTheme.spacing(10),
  shadows,
  shape: {
    borderRadius: 8,
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': [
          fonts.avenirMedium,
          fonts.avenirSemiBold,
          fonts.avenirBold,
        ],
        html: {
          WebkitFontSmoothing: 'auto',
        },
        body: {
          backgroundColor: defaultTheme.palette.common.white,
        },
      },
    },
    MuiBackdrop: {
      root: {
        backgroundColor: fade(palette.primary.dark, 0.6),
      },
    },
    MuiButton: {
      root: {
        padding: '6px 20px',
        borderRadius: 18,
        textTransform: 'none',
        boxShadow: 'none',
        '&.isActive': {
          color: palette.primary.light,
        },
      },
      text: {
        padding: '6px 20px',
        fontWeight: 600,
        '&.Mui-disabled': {
          color: palette.grey[200],
        },
      },
      label: {
        fontWeight: 600,
      },
      contained: {
        border: '1px solid transparent',
      },
      containedPrimary: {
        boxShadow: 'none',
        '&:active': {
          backgroundColor: `${palette.primary.light} !important`,
          boxShadow: 'none !important',
        },
        [defaultTheme.breakpoints.up('sm')]: {
          '&:hover': {
            backgroundColor: palette.primary.light,
            boxShadow: `4px 4px 0 ${palette.secondary.light}`,
          },
        },
      },
      sizeSmall: {
        padding: '0 16px',
      },
      outlined: {
        backgroundColor: defaultTheme.palette.common.white,
        border: `1px solid ${fade(palette.primary.main, 0.16)}`,
        '&.Mui-disabled': {
          border: `1px solid ${fade(palette.primary.main, 0.16)}`,
          color: palette.grey[200],
        },
        [defaultTheme.breakpoints.up('sm')]: {
          '&:hover': {
            boxShadow: `4px 4px 0 ${palette.secondary.light}`,
          },
        },
      },
      outlinedPrimary: {
        backgroundColor: defaultTheme.palette.common.white,
        border: `1px solid ${fade(palette.primary.main, 0.16)}`,
        '&.Mui-disabled': {
          color: palette.grey[200],
        },
        '&:active': {
          color: palette.primary.light,
          backgroundColor: `${palette.primary.lighter} !important`,
          boxShadow: 'none !important',
        },
        [defaultTheme.breakpoints.up('sm')]: {
          '&:hover': {
            backgroundColor: defaultTheme.palette.common.white,
            borderColor: fade(palette.primary.main, 0.16),
          },
        },
      },
      textPrimary: {
        '&:hover': {
          color: palette.primary.light,
          backgroundColor: palette.primary.lighter,
        },
        '&:active': {
          color: palette.primary.light,
        },
      },
    },
    MuiIconButton: {
      root: {
        border: '1px solid transparent',
        transition: defaultTheme.transitions.create(['all'], {
          easing: defaultTheme.transitions.easing.sharp,
          duration: defaultTheme.transitions.duration.shorter,
        }),
        '&.Mui-disabled': {
          color: palette.grey[200],
          backgroundColor: defaultTheme.palette.common.white,
        },
      },
    },
    MuiFab: {
      root: {
        boxShadow: shadows[2],
      },
      primary: {
        '&:active': {
          backgroundColor: `${palette.primary.light} !important`,
          boxShadow: 'none !important',
        },
        [defaultTheme.breakpoints.up('sm')]: {
          '&:hover': {
            backgroundColor: palette.primary.light,
            boxShadow: `4px 4px 0 ${palette.secondary.light}`,
          },
        },
      },
    },
    MuiLink: {
      root: {
        fontFamily: fonts.avenirSemiBold,
        fontWeight: 600,
        '&:hover': {
          cursor: 'pointer',
        },
      },
    },
    MuiBadge: {
      badge: {
        '&.MuiBadge-colorError': {
          backgroundColor: palette.error.dark,
        },
      },
    },
    MuiLinearProgress: {
      root: {
        height: 8,
        borderRadius: 4,
      },
      colorPrimary: {
        backgroundColor: palette.primary.lighter,
      },
      barColorPrimary: {
        borderRadius: 4,
        backgroundColor: palette.primary.light,
      },
    },
    MuiInputLabel: {
      root: {
        paddingLeft: defaultTheme.spacing(2),
      },
    },
    MuiFormHelperText: {
      root: {
        paddingLeft: defaultTheme.spacing(2),
      },
    },
    MuiInputBase: {
      root: {
        padding: defaultTheme.spacing('11px', 2),
        border: `1px solid ${fade(palette.primary.main, 0.16)}`,
        borderRadius: defaultTheme.spacing(1),
        transition: defaultTheme.transitions.create(
          ['background-color', 'border-color', 'color'],
          {
            easing: defaultTheme.transitions.easing.sharp,
            duration: defaultTheme.transitions.duration.shorter,
          },
        ),
        '& > fieldset': {
          border: 'none',
        },
        '&.hasValue': {
          color: palette.primary.light,
          borderColor: fade(palette.primary.main, 0.16),
          backgroundColor: palette.primary.lighter,
          '& > input': {
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${palette.primary.lighter} inset`,
            },
          },
        },
        '&.Mui-focused, &:hover:not($disabled)': {
          color: palette.primary.light,
          borderColor: palette.primary.light,
          backgroundColor: palette.primary.lighter,
          '& > input': {
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${palette.primary.lighter} inset`,
            },
          },
        },
        '&.Mui-error': {
          color: palette.error.main,
          borderColor: palette.error.main,
          backgroundColor: palette.error.light,
          '& > .MuiInputBase-input': {
            color: palette.primary.dark,
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 100px ${palette.error.light} inset`,
            },
          },
          '&:hover': {
            color: palette.error.main,
            borderColor: palette.error.main,
            backgroundColor: palette.error.light,
            '& > .MuiInputBase-input': {
              color: palette.primary.dark,
              '&:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 100px ${palette.error.light} inset`,
              },
            },
          },
        },
        '&:before, &:after': {
          display: 'none',
        },
      },
      input: {
        transition: defaultTheme.transitions.create(['all'], {
          easing: defaultTheme.transitions.easing.sharp,
          duration: defaultTheme.transitions.duration.shorter,
        }),
        '&::placeholder': {
          fontStyle: 'italic',
        },
        '&:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 100px ${defaultTheme.palette.common.white} inset`,
        },
      },
    },
    MuiAutocomplete: {
      paper: {
        '& ul': {
          maxHeight: defaultTheme.spacing(32),
        },
      },
      option: {
        '&[aria-selected="true"]': {
          color: palette.primary.light,
          backgroundColor: fade(palette.primary.lighter, 0.64),
        },
        '&[data-focus="true"]': {
          backgroundColor: fade(palette.primary.lighter, 0.3),
        },
      },
    },
    MuiSvgIcon: {
      root: {
        '& .secondaryPath': {
          fill: palette.secondary.main,
        },
      },
    },
    MuiAvatar: {
      root: {
        fontSize: 16,
        fontWeight: 600,
        '&.MuiAvatar-colorDefault': {
          backgroundColor: palette.primary.light,
          color: defaultTheme.palette.common.white,
        },
      },
      img: {
        objectFit: 'contain',
      },
    },
    MuiChip: {
      root: {
        color: palette.primary.main,
        fontSize: 12,
        fontWeight: 500,
      },
      icon: {
        color: 'inherit',
      },
      outlined: {
        border: `1px solid ${fade(palette.primary.main, 0.16)}`,
      },
    },
    MuiSelect: {
      icon: {
        marginRight: defaultTheme.spacing(2),
      },
      select: {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiSkeleton: {
      root: {
        backgroundColor: palette.primary.lighter,
      },
      circle: {
        borderRadius: 4,
      },
    },
  },
})

export default theme
